import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { StorylineApi } from '../../API/StorylineApi';
import { useAuth } from '../../Auth/AuthProvider';
import Button from '../../Components/Button';
import { AnalyticsEvent, useAnalytics } from '../../Context/AnalyticsProvider';
import { Book } from '../../Types/Book';
import { BookChapter } from '../../Types/BookChapter';
import { StorylineDilemma } from '../../Types/StorylineDilemma';
import StorylineDilemmaOptionsSelectView from '../Controls/StorylineDilemmaOptionsSelectView';

interface DilemmaBuilderInlineViewProps {
  book: Book;
  chapter: BookChapter;
  dilemma: StorylineDilemma;
  onSubmitting?: (
    dilemma: StorylineDilemma,
    selectedOptionIds: string[],
  ) => void;
  onSubmitted?: (
    dilemma: StorylineDilemma,
    selectedOptionIds: string[],
  ) => void;
  onError?: (dilemma: StorylineDilemma, selectedOptionIds: string[]) => void;
}

const DilemmaBuilderInlineView: React.FC<DilemmaBuilderInlineViewProps> = ({
  book,
  chapter,
  dilemma,
  onSubmitting,
  onSubmitted,
  onError,
}) => {
  const { trackEvent } = useAnalytics();
  const { isAuthenticated, signUpAsAnonymous } = useAuth();
  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleOptionSelect = (optionId: string) => {
    setSelectedOptionIds((prev) => {
      // If already selected, remove it
      if (prev.includes(optionId)) {
        return prev.filter((id) => id !== optionId);
      }

      // If not yet selected and we have less than 3 options, add it
      if (prev.length < 3) {
        return [...prev, optionId];
      }

      // If we already have 3 options, replace the oldest one
      return [...prev.slice(1), optionId];
    });
  };

  const canSubmit = useMemo(() => {
    return selectedOptionIds.length === 3;
  }, [selectedOptionIds]);

  async function onSubmit() {
    if (!canSubmit) {
      toast.info('Please select exactly 3 options');
      return;
    }

    try {
      setIsSubmitting(true);
      onSubmitting?.(dilemma, selectedOptionIds);

      // Track dilemma choices submission
      await trackEvent(AnalyticsEvent.DILEMMA_CHOICES_SELECTION_REQUESTED, {
        source: 'DilemmaBuilderInlineView',
        book: book,
        chapter: chapter,
        dilemmaId: dilemma.id,
        dilemmaQuestion: dilemma.question,
        selectedOptionIds: selectedOptionIds,
      });

      if (!isAuthenticated) {
        await signUpAsAnonymous();
      }

      const storylineApi = new StorylineApi();

      // Here we would call the API to save the selected options
      // This endpoint would need to be implemented
      await storylineApi.selectDilemmaOptions(dilemma.id, selectedOptionIds);

      await trackEvent(AnalyticsEvent.DILEMMA_CHOICES_SELECTION_COMPLETED, {
        source: 'DilemmaBuilderInlineView',
        book: book,
        chapter: chapter,
        dilemmaId: dilemma.id,
        selectedOptionIds: selectedOptionIds,
      });

      onSubmitted?.(dilemma, selectedOptionIds);
    } catch (error) {
      console.error('Error submitting dilemma choices:', error);

      // Show toast notification
      toast.error('Unable to save your choices. Please try again.');

      await trackEvent(AnalyticsEvent.DILEMMA_CHOICES_SELECTION_FAILED, {
        source: 'DilemmaBuilderInlineView',
        book: book,
        chapter: chapter,
        dilemmaId: dilemma.id,
        error: error?.toString() || 'Unknown Error',
      });

      setIsSubmitting(false);
      onError?.(dilemma, selectedOptionIds);
    }
  }

  return (
    <div className="flex flex-col gap-2 p-4 bg-white rounded-xl">
      <h2 className="text-2xl font-bold">What happens next?</h2>
      <p className="text-gray-600">Send your friend 3 options!</p>

      <div className="space-y-4 pt-2">
        <StorylineDilemmaOptionsSelectView
          gridCss={'grid-cols-2 gap-3'}
          dilemma={dilemma}
          onOptionSelect={handleOptionSelect}
          selectedOptionIds={selectedOptionIds}
          allowCreate={true}
          showDilemmaDetails={false}
          maxSelectedOptions={3}
          minSelectedOptions={0}
        />
      </div>

      <div className="mt-4 flex flex-col items-center gap-4">
        <Button
          onClick={onSubmit}
          variant="primary"
          className="w-full max-w-xs"
          disabled={!canSubmit}
          isInProgress={isSubmitting}
          inProgressText="Sending options..."
        >
          Send Options
        </Button>
      </div>
    </div>
  );
};

export default DilemmaBuilderInlineView;
